import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback } from "react";
import { getInforme, type InformeData } from "@/lib/pagos.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, TrendingUp, Euro, BedDouble, Percent } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gestion/informes")({
  component: InformesPage,
});

const fmtEur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtNum = (n: number, d = 1) => n.toFixed(d);

function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}
function addDays(iso: string, n: number) {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function Kpi({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</CardTitle>
        <Icon className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="font-serif text-2xl">{value}</div>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function exportCSV(data: InformeData) {
  const rows: string[] = [];
  rows.push(`Informe ${data.rango.desde} a ${data.rango.hasta}`);
  rows.push("");
  rows.push("KPI;Valor");
  rows.push(`Ingresos totales;${data.ingresos_totales.toFixed(2)}`);
  rows.push(`Cobrado;${data.ingresos_cobrados.toFixed(2)}`);
  rows.push(`Pendiente;${data.ingresos_pendientes.toFixed(2)}`);
  rows.push(`Reservas;${data.num_reservas}`);
  rows.push(`Noches vendidas;${data.num_noches_vendidas}`);
  rows.push(`Noches disponibles;${data.noches_disponibles}`);
  rows.push(`Ocupación %;${data.ocupacion_pct.toFixed(2)}`);
  rows.push(`ADR;${data.adr.toFixed(2)}`);
  rows.push(`RevPAR;${data.revpar.toFixed(2)}`);
  rows.push(`Estancia media;${data.estancia_media.toFixed(2)}`);
  rows.push("");
  rows.push("Por canal");
  rows.push("Canal;Reservas;Ingresos");
  for (const c of data.por_canal) rows.push(`${c.canal};${c.reservas};${c.ingresos.toFixed(2)}`);
  rows.push("");
  rows.push("Por habitación");
  rows.push("Habitación;Reservas;Noches;Ingresos;Ocupación %");
  for (const h of data.por_habitacion) rows.push(`${h.nombre};${h.reservas};${h.noches};${h.ingresos.toFixed(2)};${h.ocupacion_pct.toFixed(2)}`);
  rows.push("");
  rows.push("Por mes");
  rows.push("Mes;Ingresos;Noches");
  for (const m of data.por_mes) rows.push(`${m.mes};${m.ingresos.toFixed(2)};${m.noches}`);
  rows.push("");
  rows.push("Cobros por método (en el rango)");
  rows.push("Método;Importe");
  for (const m of data.por_metodo_pago) rows.push(`${m.metodo};${m.importe.toFixed(2)}`);

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `informe-${data.rango.desde}-${data.rango.hasta}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function InformesPage() {
  const fn = useServerFn(getInforme);
  const [desde, setDesde] = useState(firstDayOfMonth);
  const [hasta, setHasta] = useState(() => addDays(today(), 1));
  const [data, setData] = useState<InformeData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fn({ data: { desde, hasta } })
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(e?.message ?? "error"))
      .finally(() => setLoading(false));
  }, [fn, desde, hasta]);

  useEffect(() => { load(); }, [load]);

  function setPreset(p: "hoy" | "7d" | "mes" | "30d" | "anyo") {
    const t = today();
    if (p === "hoy") { setDesde(t); setHasta(addDays(t, 1)); }
    if (p === "7d") { setDesde(addDays(t, -7)); setHasta(addDays(t, 1)); }
    if (p === "30d") { setDesde(addDays(t, -30)); setHasta(addDays(t, 1)); }
    if (p === "mes") { setDesde(firstDayOfMonth()); setHasta(addDays(t, 1)); }
    if (p === "anyo") {
      const d = new Date();
      setDesde(new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10));
      setHasta(addDays(t, 1));
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl">Informes</h2>
        <p className="text-sm text-muted-foreground">Ingresos, ocupación y KPIs hoteleros (ADR, RevPAR).</p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label className="text-xs text-muted-foreground">Desde</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="block rounded-md border bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Hasta (exclusivo)</label>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="block rounded-md border bg-background px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setPreset("hoy")}>Hoy</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("7d")}>7d</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("30d")}>30d</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("mes")}>Mes</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("anyo")}>Año</Button>
          </div>
          <div className="ml-auto flex gap-2">
            <Button onClick={load} disabled={loading}>Actualizar</Button>
            {data && <Button variant="outline" onClick={() => exportCSV(data)}><Download className="mr-1 h-4 w-4" /> CSV</Button>}
          </div>
        </CardContent>
      </Card>

      {err && <p className="text-sm text-destructive">{err}</p>}
      {!data ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Euro} label="Ingresos" value={fmtEur(data.ingresos_totales)} sub={`Cobrado ${fmtEur(data.ingresos_cobrados)} · Pdte ${fmtEur(data.ingresos_pendientes)}`} />
            <Kpi icon={Percent} label="Ocupación" value={fmtPct(data.ocupacion_pct)} sub={`${data.num_noches_vendidas} / ${data.noches_disponibles} noches`} />
            <Kpi icon={TrendingUp} label="ADR" value={fmtEur(data.adr)} sub="Precio medio por noche" />
            <Kpi icon={BedDouble} label="RevPAR" value={fmtEur(data.revpar)} sub={`Estancia media ${fmtNum(data.estancia_media)} noches`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Por canal</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr><th className="text-left">Canal</th><th className="text-right">Reservas</th><th className="text-right">Ingresos</th></tr>
                  </thead>
                  <tbody>
                    {data.por_canal.map((c) => (
                      <tr key={c.canal} className="border-t"><td className="py-1.5">{c.canal}</td><td className="text-right">{c.reservas}</td><td className="text-right">{fmtEur(c.ingresos)}</td></tr>
                    ))}
                    {data.por_canal.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-xs text-muted-foreground">Sin datos</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Cobros por método (en el rango)</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr><th className="text-left">Método</th><th className="text-right">Importe</th></tr>
                  </thead>
                  <tbody>
                    {data.por_metodo_pago.map((m) => (
                      <tr key={m.metodo} className="border-t"><td className="py-1.5">{m.metodo}</td><td className="text-right">{fmtEur(m.importe)}</td></tr>
                    ))}
                    {data.por_metodo_pago.length === 0 && <tr><td colSpan={2} className="py-3 text-center text-xs text-muted-foreground">Sin cobros</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Por habitación</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr><th className="text-left">Habitación</th><th className="text-right">Reservas</th><th className="text-right">Noches</th><th className="text-right">Ingresos</th><th className="text-right">Ocupación</th></tr>
                </thead>
                <tbody>
                  {data.por_habitacion.map((h) => (
                    <tr key={h.habitacion_id} className="border-t">
                      <td className="py-1.5">{h.nombre}</td>
                      <td className="text-right">{h.reservas}</td>
                      <td className="text-right">{h.noches}</td>
                      <td className="text-right">{fmtEur(h.ingresos)}</td>
                      <td className="text-right">{fmtPct(h.ocupacion_pct)}</td>
                    </tr>
                  ))}
                  {data.por_habitacion.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-xs text-muted-foreground">Sin reservas en el rango</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Por mes</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr><th className="text-left">Mes</th><th className="text-right">Ingresos</th><th className="text-right">Noches</th></tr>
                </thead>
                <tbody>
                  {data.por_mes.map((m) => (
                    <tr key={m.mes} className="border-t"><td className="py-1.5">{m.mes}</td><td className="text-right">{fmtEur(m.ingresos)}</td><td className="text-right">{m.noches}</td></tr>
                  ))}
                  {data.por_mes.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-xs text-muted-foreground">Sin datos</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}